/**
 * Settings → Super-admin → Workspaces: een archief downloaden, een workspace
 * verwijderen (pas na het archief, met de naam ter bevestiging) en een
 * archief terugzetten. Het werk zelf zit in `@ae/api-client` (archive.ts),
 * gedeeld met de desktop-app.
 */
import {
  type ArchiveProgress,
  archiveFileName,
  deleteWorkspace,
  downloadWorkspaceArchive,
  parseWorkspaceArchive,
  restoreWorkspaceArchive,
  type WorkspaceArchive,
} from "@ae/api-client";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { ApiError, webRequester } from "../api/client";
import { useSession } from "../state/session";
import { ld, pill } from "./ledgerUi";

function explain(err: unknown): string {
  if (err instanceof ApiError) return err.body?.detail ?? err.body?.title ?? err.message;
  if (err instanceof Error) return err.message;
  return String(err);
}

function save(archive: WorkspaceArchive): void {
  const blob = new Blob([JSON.stringify(archive)], { type: "application/json" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = archiveFileName(archive);
  document.body.appendChild(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 10_000);
}

const totalRows = (a: { tables: Array<{ rows: number }> }) =>
  a.tables.reduce((n, t) => n + t.rows, 0);

export function ArchiveSection({ onOpen }: { onOpen: (workspaceId: string) => void }) {
  const { t } = useTranslation();
  const me = useSession((s) => s.me);
  const [busy, setBusy] = useState(false);
  const [progress, setProgress] = useState<ArchiveProgress | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [message, setMessage] = useState<string | null>(null);
  const [deleting, setDeleting] = useState<{
    id: string;
    name: string;
    archived: boolean;
    confirm: string;
  } | null>(null);
  const [restoring, setRestoring] = useState<WorkspaceArchive | null>(null);
  const [restored, setRestored] = useState<{ id: string; mine: boolean } | null>(null);

  const run = async (fn: () => Promise<void>) => {
    setBusy(true);
    setError(null);
    setMessage(null);
    try {
      await fn();
    } catch (err) {
      setError(explain(err));
    } finally {
      setBusy(false);
      setProgress(null);
    }
  };

  const download = (id: string, then?: () => void) =>
    run(async () => {
      const archive = await downloadWorkspaceArchive(webRequester, id, setProgress);
      save(archive);
      setMessage(
        t("settings.superAdmin.archiveSaved", {
          name: archive.workspace.name,
          file: archiveFileName(archive),
          rows: totalRows(archive),
        }),
      );
      then?.();
    });

  const remove = () =>
    run(async () => {
      if (!deleting) return;
      await deleteWorkspace(webRequester, deleting.id, deleting.confirm.trim());
      setMessage(t("settings.superAdmin.deleteDone", { name: deleting.name }));
      setDeleting(null);
      await useSession.getState().refreshMe();
    });

  const pick = async (file: File | undefined) => {
    setError(null);
    setMessage(null);
    setRestored(null);
    if (!file) return;
    try {
      setRestoring(parseWorkspaceArchive(await file.text()));
    } catch (err) {
      setRestoring(null);
      setError(explain(err));
    }
  };

  const restore = () =>
    run(async () => {
      if (!restoring) return;
      const result = await restoreWorkspaceArchive(webRequester, restoring, setProgress);
      setMessage(
        result.ok
          ? t("settings.superAdmin.restoreDone", { name: restoring.workspace.name })
          : t("settings.superAdmin.restoreMismatch", {
              name: restoring.workspace.name,
              tables: result.mismatches
                .map((m) => `${m.table} ${m.actual}/${m.expected}`)
                .join(", "),
            }),
      );
      const email = me?.email?.toLowerCase();
      const mine = (restoring.data.workspace_members ?? []).some(
        (m) => String(m.email).toLowerCase() === email,
      );
      setRestored({ id: restoring.workspace.id, mine });
      setRestoring(null);
      await useSession.getState().refreshMe();
    });

  return (
    <section className="flex flex-col gap-4 border-(--ld-rule) border-t pt-8">
      <div className="max-w-xl">
        <h4 className={ld.sectionTitle}>{t("settings.superAdmin.archiveHeading")}</h4>
        <p className={ld.sectionIntro}>{t("settings.superAdmin.archiveIntro")}</p>
      </div>

      <ul className={ld.list}>
        {(me?.workspaces ?? []).map((w) => (
          <li
            key={w.workspace_id}
            className="flex min-h-12 items-center justify-between gap-3 px-4 py-2"
          >
            <span className="truncate text-sm">{w.workspace_name}</span>
            <span className="flex shrink-0 items-center gap-4">
              <button
                type="button"
                className={ld.textDanger}
                disabled={busy}
                onClick={() => {
                  setMessage(null);
                  setDeleting({
                    id: w.workspace_id,
                    name: w.workspace_name,
                    archived: false,
                    confirm: "",
                  });
                }}
              >
                {t("settings.superAdmin.deleteButton")}
              </button>
              <button
                type="button"
                className={ld.secondarySm}
                disabled={busy}
                onClick={() => void download(w.workspace_id)}
              >
                {t("settings.superAdmin.archiveDownload")}
              </button>
            </span>
          </li>
        ))}
      </ul>

      {deleting && (
        <div className="flex max-w-xl flex-col gap-4 rounded-lg border border-(--ld-negative) p-4">
          <div>
            <h5 className="font-medium text-(--ld-negative) text-sm">
              {t("settings.superAdmin.deleteHeading", { name: deleting.name })}
            </h5>
            <p className={ld.sectionIntro}>{t("settings.superAdmin.deleteWarning")}</p>
          </div>
          <div>
            {deleting.archived ? (
              <span className={pill.positive}>{t("settings.superAdmin.deleteArchived")}</span>
            ) : (
              <button
                type="button"
                className={ld.secondarySm}
                disabled={busy}
                onClick={() =>
                  void download(deleting.id, () =>
                    setDeleting((d) => (d ? { ...d, archived: true } : d)),
                  )
                }
              >
                {t("settings.superAdmin.archiveDownload")}
              </button>
            )}
          </div>
          <div>
            <label htmlFor="sa-delete-confirm" className={ld.label}>
              {t("settings.superAdmin.deleteConfirm", { name: deleting.name })}
            </label>
            <input
              id="sa-delete-confirm"
              autoComplete="off"
              value={deleting.confirm}
              disabled={!deleting.archived}
              onChange={(e) => setDeleting({ ...deleting, confirm: e.target.value })}
              className={ld.input}
            />
          </div>
          <div className="flex gap-2">
            <button
              type="button"
              disabled={busy || !deleting.archived || deleting.confirm.trim() !== deleting.name}
              onClick={() => void remove()}
              className={ld.dangerFilled}
            >
              {t("settings.superAdmin.deleteFinal")}
            </button>
            <button
              type="button"
              className={ld.secondary}
              disabled={busy}
              onClick={() => setDeleting(null)}
            >
              {t("settings.superAdmin.cancel")}
            </button>
          </div>
        </div>
      )}

      <div className="mt-4 flex max-w-xl flex-col gap-3">
        <h4 className={ld.sectionTitle}>{t("settings.superAdmin.restoreHeading")}</h4>
        <p className={ld.sectionIntro}>{t("settings.superAdmin.restoreIntro")}</p>
        <input
          type="file"
          accept=".json,application/json"
          aria-label={t("settings.superAdmin.restoreHeading")}
          disabled={busy}
          onChange={(e) => void pick(e.target.files?.[0])}
          className="text-(--ld-muted) text-sm file:mr-3 file:h-8 file:cursor-pointer file:rounded-md file:border file:border-(--ld-control) file:border-solid file:bg-(--ld-surface) file:px-3 file:font-medium file:text-(--ld-text) file:text-[13px] hover:file:bg-(--ld-raised)"
        />
        {restoring && (
          <div className={`${ld.well} flex flex-col items-start gap-3 text-sm`}>
            <p>
              {t("settings.superAdmin.restoreSummary", {
                name: restoring.workspace.name,
                date: restoring.exported_at.slice(0, 16).replace("T", " "),
                rows: totalRows(restoring),
                tables: restoring.tables.filter((x) => x.rows > 0).length,
              })}
            </p>
            <button
              type="button"
              disabled={busy}
              onClick={() => void restore()}
              className={ld.primary}
            >
              {t("settings.superAdmin.restoreButton")}
            </button>
          </div>
        )}
      </div>

      {progress && (
        <p role="status" className="text-(--ld-muted) text-sm tabular-nums">
          {t(`settings.superAdmin.progress_${progress.phase}`)} {progress.table ?? ""} —{" "}
          {progress.done}/{progress.total}
        </p>
      )}
      {message && (
        <div role="status" className={`flex items-center justify-between gap-3 ${ld.success}`}>
          <span>{message}</span>
          {restored?.mine && (
            <button
              type="button"
              onClick={() => onOpen(restored.id)}
              className="shrink-0 font-medium underline-offset-2 hover:underline"
            >
              {t("settings.superAdmin.workspacesOpen")}
            </button>
          )}
        </div>
      )}
      {error && (
        <p role="alert" className={ld.error}>
          {error}
        </p>
      )}
    </section>
  );
}
